import { createNativeStackNavigator } from '@react-navigation/native-stack';
import * as React from 'react';
import { BillPaymentStackRoutes, BillPaymentStackScreenProps } from '@/features/bill-payment/payment.navigator';
import { UnionPayStackParamList, UnionPayStackRoutes } from '@/features/bill-payment/screens/union-pay/type';
import { useCallback, useContext, useEffect, useState } from 'react';
import { LayoutContext } from 'contexts/LayoutContext';
import { UnionPayRemittanceScreen } from '@/features/bill-payment/screens/union-pay/UnionPayRemittance.screen';
import { UnionPayMoreScreen } from '@/features/bill-payment/screens/union-pay/UnionPayMore.screen';
import { InformationScreen } from '@/features/bill-payment/screens/union-pay/Information.screen';
import { ConfirmationScreen } from '@/features/bill-payment/screens/union-pay/Confirmation.screen';
import { PlacedScreen } from '@/features/bill-payment/screens/union-pay/Placed.screen';
import { SelectAccountScreen } from '@/features/bill-payment/screens/union-pay/SelectAccount.screen';
import { AddNewAccountScreen } from '@/features/bill-payment/screens/union-pay/AddNewAccount.screen';
import { ModalContainerView } from '@/features/bill-payment/screens/components/ModalContainerView';
import { View } from 'dripsy';
import { AccountSelectScreen } from '@/features/bill-payment/screens/union-pay/AccountSelect.screen';
import { TWOFAScreen } from '@/features/bill-payment/screens/union-pay/TWOFA.screen';
import { UnionType } from '@/features/bill-payment/enum';

const UnionPayStack = createNativeStackNavigator<UnionPayStackParamList>();

export const UnionPayNavigator: React.FC<BillPaymentStackScreenProps<BillPaymentStackRoutes.UnionPay>> = ({ route }) => {
  const {
    onSelect,
    asset,
    onAccountSubmit,
    onBack,
    transferPeerCountFlag,
    transferPeer,
    wallet,
    calcFee,
    beneficiaryInformation,
    refId,
    onChangeView,
    unionType = UnionType.PAYMENT,
    refresh
  } = route.params || {};
  const layout = useContext(LayoutContext);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!layout.isMobile) return;
    setDimensions(layout.dimensions);
  }, [layout.dimensions]);

  const onLayout = useCallback((event) => {
    const { width, height } = event.nativeEvent.layout;
    setDimensions({ width, height });
  }, []);

  return (
    <ModalContainerView dimensions={dimensions}>
      <View sx={{ height: dimensions.height, width: dimensions.width }} onLayout={onLayout}>
        <UnionPayStack.Navigator
          initialRouteName={UnionPayStackRoutes.UnionPayRemittance}
          screenOptions={{
            presentation: 'transparentModal',
            headerShown: false
          }}>
          <UnionPayStack.Screen
            name={UnionPayStackRoutes.UnionPayRemittance}
            component={UnionPayRemittanceScreen}
            initialParams={{ onLayout, unionType, refresh }}
          />
          <UnionPayStack.Screen
            name={UnionPayStackRoutes.UnionPayMore}
            component={UnionPayMoreScreen}
            initialParams={{}}
            options={{ presentation: layout.isMobile ? 'modal' : 'transparentModal', headerShown: false }}
          />
          <UnionPayStack.Screen
            name={UnionPayStackRoutes.Information}
            component={InformationScreen}
            initialParams={{ onLayout, onBack, transferPeer, wallet, calcFee, onChangeView, transferPeerCountFlag, unionType, refresh }}
            options={{
              presentation: 'transparentModal',
              headerShown: false
            }}
          />
          <UnionPayStack.Screen
            name={UnionPayStackRoutes.Confirmation}
            component={ConfirmationScreen}
            initialParams={{ onLayout, onBack, wallet, calcFee, beneficiaryInformation, onChangeView, unionType, refresh }}
            options={{ presentation: layout.isMobile ? 'modal' : 'transparentModal', headerShown: false }}
          />
          <UnionPayStack.Screen
            name={UnionPayStackRoutes.Placed}
            component={PlacedScreen}
            initialParams={{ onLayout, onBack, wallet, calcFee, beneficiaryInformation, refId, unionType, refresh }}
            options={{ presentation: 'transparentModal', headerShown: false }}
          />
          <UnionPayStack.Screen
            name={UnionPayStackRoutes.SelectAccount}
            component={SelectAccountScreen}
            initialParams={{ onLayout, onSelect, onBack, onChangeView }}
            options={{ presentation: 'transparentModal', headerShown: false }}
          />
          <UnionPayStack.Screen
            name={UnionPayStackRoutes.AddNewAccount}
            component={AddNewAccountScreen}
            initialParams={{ onLayout, onAccountSubmit, transferPeerCountFlag, onBack, wallet, calcFee, onChangeView, unionType, refresh }}
            options={{ presentation: 'transparentModal', headerShown: false }}
          />
          <UnionPayStack.Screen
            name={UnionPayStackRoutes.AccountSelect}
            component={AccountSelectScreen}
            initialParams={{ onLayout, onBack, onSelect, asset }}
            options={{ presentation: 'transparentModal', headerShown: false }}
          />
          <UnionPayStack.Screen
            name={UnionPayStackRoutes.TWOFA}
            component={TWOFAScreen}
            initialParams={{ onLayout, onBack, wallet, calcFee, beneficiaryInformation, onChangeView, unionType, refresh }}
            options={{ presentation: 'transparentModal', headerShown: false }}
          />
        </UnionPayStack.Navigator>
      </View>
    </ModalContainerView>
  );
};
